// API service layer for communicating with CloudFlare Worker backend
import { API_ENDPOINTS } from '@/config/api'
import { bitable } from '@lark-base-open/js-sdk'
import { useUIStore } from '@/stores/ui'
import type {
  Template,
  Tenant,
  Base,
  GenerationRun,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  UpdateTenantRequest,
  AdminUpdateTenantRequest,
  GetTenantInfoResponse
} from '@/types'

/**
 * Get authentication headers from Lark context
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const [tenantKey, userId, selection] = await Promise.all([
    bitable.bridge.getTenantKey(),
    bitable.bridge.getUserId(),
    bitable.base.getSelection(),
  ])

  if (!selection.baseId) {
    throw new Error('No base selected')
  }

  return {
    'X-Lark-Tenant-Key': tenantKey,
    'X-Lark-User-Id': userId,
    'X-Lark-Base-Token': selection.baseId,
  }
}

/**
 * Base fetch wrapper with error handling and auth
 */
async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const authHeaders = await getAuthHeaders()
  const uiStore = useUIStore()

  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...options.headers,
    },
    ...options,
  }

  uiStore.startLoading()
  try {
    const response = await fetch(endpoint, config)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }))
      // Backend returns either { error: "..." } or { message: "..." }
      const errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
      throw new Error(errorMessage)
    }

    return await response.json() as T
  } catch (error) {
    console.error('API request failed:', error)
    throw error
  } finally {
    uiStore.stopLoading()
  }
}

/**
 * Template APIs
 */
export const templateApi = {
  async list(baseToken: string, tableId: string): Promise<Template[]> {
    return apiFetch<Template[]>(API_ENDPOINTS.getTemplates(baseToken, tableId))
  },

  async create(baseToken: string, tableId: string, templateData: CreateTemplateRequest): Promise<Template> {
    return apiFetch<Template>(API_ENDPOINTS.createTemplate(baseToken, tableId), {
      method: 'POST',
      body: JSON.stringify(templateData),
    })
  },

  async update(baseToken: string, tableId: string, templateId: string, templateData: UpdateTemplateRequest): Promise<Template> {
    return apiFetch<Template>(API_ENDPOINTS.updateTemplate(baseToken, tableId, templateId), {
      method: 'PUT',
      body: JSON.stringify(templateData),
    })
  },

  async delete(baseToken: string, tableId: string, templateId: string): Promise<void> {
    return apiFetch<void>(API_ENDPOINTS.deleteTemplate(baseToken, tableId, templateId), {
      method: 'DELETE',
    })
  },

  /**
   * Upload template file to R2
   */
  async uploadFile(baseToken: string, tableId: string, templateId: string, file: File): Promise<Template> {
    const authHeaders = await getAuthHeaders()
    const uiStore = useUIStore()
    const formData = new FormData()
    formData.append('file', file)

    uiStore.startLoading()
    try {
      const response = await fetch(API_ENDPOINTS.uploadTemplate(baseToken, tableId, templateId), {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }))
        // If validation errors exist, preserve them in the thrown error
        if (errorData.validationErrors) {
          const error = new Error(errorData.error || 'Upload failed') as any
          error.validationErrors = errorData.validationErrors
          throw error
        }
        throw new Error(errorData.message || errorData.error || `Upload failed: ${response.statusText}`)
      }
      return await response.json() as Template
    } finally {
      uiStore.stopLoading()
    }
  },

  /**
   * Download template file from R2
   */
  async downloadFile(baseToken: string, tableId: string, templateId: string, fileName: string): Promise<void> {
    const authHeaders = await getAuthHeaders()
    const uiStore = useUIStore()

    uiStore.startLoading()
    try {
      const response = await fetch(API_ENDPOINTS.downloadTemplate(baseToken, tableId, templateId), {
        method: 'GET',
        headers: authHeaders,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }))
        throw new Error(error.message || `Download failed: ${response.statusText}`)
      }

      // Create blob and trigger download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName || 'template.docx'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } finally {
      uiStore.stopLoading()
    }
  },

  /**
   * Get generation runs with pagination
   * Returns signed download URLs for completed generations
   */
  async getGenerations(
    baseToken: string,
    tableId: string,
    page: number = 1,
    limit: number = 20,
    templateId?: string
  ): Promise<{ data: GenerationRun[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (templateId) {
      params.append('templateId', templateId)
    }
    return apiFetch<{ data: GenerationRun[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(
      `${API_ENDPOINTS.getGenerations(baseToken, tableId)}?${params.toString()}`
    )
  },
}

/**
 * Tenant APIs - for managing current tenant and base info
 */
export const tenantApi = {
  async getTenantInfo(): Promise<GetTenantInfoResponse> {
    return apiFetch<GetTenantInfoResponse>(API_ENDPOINTS.getTenantInfo())
  },

  async updateTenantInfo(updates: UpdateTenantRequest & { personal_base_token?: string }): Promise<GetTenantInfoResponse> {
    return apiFetch<GetTenantInfoResponse>(API_ENDPOINTS.updateTenantInfo(), {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  },

  async getTenantTemplates(): Promise<{ templates: (Template & { lark_base_token: string })[] }> {
    return apiFetch<{ templates: (Template & { lark_base_token: string })[] }>(API_ENDPOINTS.getTenantTemplates())
  },
}

/**
 * Admin API - requires API key passed as parameter
 */
async function adminApiFetch<T>(apiKey: string, endpoint: string, options: RequestInit = {}): Promise<T> {
  const uiStore = useUIStore()
  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-API-Key': apiKey,
      ...options.headers,
    },
    ...options,
  }

  uiStore.startLoading()
  try {
    const response = await fetch(endpoint, config)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }))
      // Backend returns either { error: "..." } or { message: "..." }
      const errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`
      throw new Error(errorMessage)
    }

    return await response.json() as T
  } catch (error) {
    console.error('Admin API request failed:', error)
    throw error
  } finally {
    uiStore.stopLoading()
  }
}

/**
 * Admin APIs
 */
export const adminApi = {
  async listTenants(apiKey: string): Promise<Tenant[]> {
    const result = await adminApiFetch<{ tenants?: Tenant[] }>(apiKey, API_ENDPOINTS.adminListTenants())
    return result.tenants || []
  },

  async getTenant(apiKey: string, tenantKey: string): Promise<Tenant> {
    const result = await adminApiFetch<{ tenant: Tenant }>(apiKey, API_ENDPOINTS.adminGetTenant(tenantKey))
    return result.tenant
  },

  async updateTenant(apiKey: string, tenantKey: string, updates: AdminUpdateTenantRequest): Promise<Tenant> {
    const result = await adminApiFetch<{ tenant: Tenant }>(apiKey, API_ENDPOINTS.adminUpdateTenant(tenantKey), {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
    return result.tenant
  },

  async getTenantBases(apiKey: string, tenantKey: string): Promise<Base[]> {
    const result = await adminApiFetch<{ bases?: Base[] }>(apiKey, API_ENDPOINTS.adminGetTenantBases(tenantKey))
    return result.bases || []
  },

  async getTenantUsers(apiKey: string, tenantKey: string): Promise<any[]> {
    const result = await adminApiFetch<{ users?: any[] }>(apiKey, API_ENDPOINTS.adminGetTenantUsers(tenantKey))
    return result.users || []
  },

  async getTenantTables(apiKey: string, tenantKey: string): Promise<any[]> {
    const result = await adminApiFetch<{ tables?: any[] }>(apiKey, API_ENDPOINTS.adminGetTenantTables(tenantKey))
    return result.tables || []
  },

  async getTenantGenerations(
    apiKey: string,
    tenantKey: string,
    page: number = 1,
    limit: number = 50
  ): Promise<{ generations: GenerationRun[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const result = await adminApiFetch<{
      generations?: GenerationRun[];
      pagination?: { page: number; limit: number; total: number; totalPages: number }
    }>(apiKey, `${API_ENDPOINTS.adminGetTenantGenerations(tenantKey)}?page=${page}&limit=${limit}`)
    return {
      generations: result.generations || [],
      pagination: result.pagination || { page: 1, limit, total: 0, totalPages: 0 }
    }
  },

  async getTenantTemplates(apiKey: string, tenantKey: string): Promise<Template[]> {
    const result = await adminApiFetch<{ templates?: Template[] }>(apiKey, API_ENDPOINTS.adminGetTenantTemplates(tenantKey))
    return result.templates || []
  },

  async triggerQuotaUpdate(apiKey: string): Promise<{ success: boolean; message: string; results: any[] }> {
    return adminApiFetch<{ success: boolean; message: string; results: any[] }>(apiKey, API_ENDPOINTS.adminTriggerQuotaUpdate())
  },

  async triggerCleanup(apiKey: string): Promise<{ success: boolean; message: string; results: any }> {
    return adminApiFetch<{ success: boolean; message: string; results: any }>(apiKey, API_ENDPOINTS.adminTriggerCleanup())
  },

  async getWorkerStatus(apiKey: string): Promise<{ success: boolean; workerUrl: string; activeJobCount?: number; jobs?: any[] }> {
    return adminApiFetch<{ success: boolean; workerUrl: string; activeJobCount?: number; jobs?: any[] }>(apiKey, API_ENDPOINTS.adminGetWorkerStatus())
  },

  async downloadTemplate(apiKey: string, templateId: string, filename?: string): Promise<void> {
    const uiStore = useUIStore()

    uiStore.startLoading()
    try {
      const response = await fetch(API_ENDPOINTS.adminDownloadTemplate(templateId), {
        method: 'GET',
        headers: {
          'X-Admin-API-Key': apiKey,
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }))
        throw new Error(error.message || `Download failed: ${response.statusText}`)
      }

      // Extract filename from Content-Disposition header or use provided filename
      const contentDisposition = response.headers.get('Content-Disposition')
      let downloadFilename = filename || 'template.docx'
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/)
        if (match && match[1]) {
          downloadFilename = match[1]
        }
      }

      // Create blob and trigger download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = downloadFilename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } finally {
      uiStore.stopLoading()
    }
  },

  async downloadGeneration(apiKey: string, generationId: string, filename?: string): Promise<void> {
    const uiStore = useUIStore()

    uiStore.startLoading()
    try {
      const response = await fetch(API_ENDPOINTS.adminDownloadGeneration(generationId), {
        method: 'GET',
        headers: {
          'X-Admin-API-Key': apiKey,
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }))
        throw new Error(error.message || `Download failed: ${response.statusText}`)
      }

      // Extract filename from Content-Disposition header or use provided filename
      const contentDisposition = response.headers.get('Content-Disposition')
      let downloadFilename = filename || 'generated-document.docx'
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="([^"]+)"/)
        if (match && match[1]) {
          downloadFilename = match[1]
        }
      }

      // Create blob and trigger download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = downloadFilename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } finally {
      uiStore.stopLoading()
    }
  },

  async generateWebhookApiKey(apiKey: string, tenantKey: string): Promise<string> {
    const result = await adminApiFetch<{ webhook_api_key: string }>(apiKey, API_ENDPOINTS.adminGenerateWebhookApiKey(tenantKey), {
      method: 'POST',
    })
    return result.webhook_api_key
  },
}
